import { Tabs } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { ProgressBarStep, ProgressBarStepItem } from '../../../../../global/components/progress-bar-step';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { genericStepperStyles } from '../../../../../global/constants/theme/styles/components/generic-stepper';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { ClergyTypeDefaultLabel, Completed, EmploymentCompensation, EmploymentTypeDefaultLabel, IfApplicable, InProgress, NonStipPosition, NotStarted, PersonalInfo } from '../../../constants/constant-strings';
import { NewHireProps } from '../../../models/arc-epi-props';
import { NewHireStep } from '../models/new-hire-model';
import { NewHireState } from '../models/new-hire-state-model';
import { EmploymentType } from '../models/new-hire-form-model';

interface Props {
	nonStip: boolean;
	personalInfoClick: () => any;
	employmentTypeClick: () => any;
	clergyTypeClick: () => any;
	subProgressName: string;
}

const normalizedCompensation = 'EmploymentCompensation';

const stipSteps: string[] = [NewHireStep.PersonalInfo, NewHireStep.EmploymentType, NewHireStep.ClergyType, normalizedCompensation];
const nonStipSteps: string[] = [NewHireStep.PersonalInfo, NewHireStep.EmploymentType, normalizedCompensation];

function getCurrentStepId(nonStip: boolean, currentStep: string) {
	if (nonStip) {
		return nonStipSteps.indexOf(currentStep);
	}

	return stipSteps.indexOf(currentStep);
}

function normalizeSteps(currentStep): string {

	switch (currentStep) {
		case NewHireStep.HireDetails:
		case NewHireStep.EmploymentCompensationLay:
		case NewHireStep.EmploymentCompensationClergy:

			return normalizedCompensation;

		case NewHireStep.PersonReviewExternal:
		case NewHireStep.PersonReviewInternal:
			return NewHireStep.PersonalInfo as string;

		default:
			return currentStep as string;
	}
}

export function NewHireProgressBar(props: Props) {
	const { nonStip, personalInfoClick, employmentTypeClick, clergyTypeClick, subProgressName } = props;

	const isClergy = subProgressName === EmploymentType.Clergy;

	const newHireState = useSelector<TcsmStates, NewHireState>(state => state.newHireState);
	const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state.epiProps?.newHireSettingsState?.epiContent);
	const classes = genericStepperStyles();
	const currentStep = newHireState?.currentStep;
	const normalizedStep = normalizeSteps(currentStep);
	const buttonClasses = buttonStyles();

	const stepIndex = getCurrentStepId(nonStip, normalizedStep);

	const getProgress = (idx: number, current: number, nonStipInProgress?: string) => {
		if (idx === current) return nonStipInProgress || newHireProps?.stepInProgressLabel || InProgress;

		return idx > current ? (newHireProps?.stepNotStartedLabel || NotStarted) : (newHireProps?.stepCompleteProgressLabel || Completed);
	};

	const getStepId = (idx: number, current: number) => {
		if (idx === current) return ProgressBarStep.INPROGRESS;
		return idx > current ? ProgressBarStep.NOTSTARTED : ProgressBarStep.COMPLETED;
	};

	const getStepNameNode = (name: string, showLink: boolean, onClick: any) => {

		if (showLink) {
			return (
				<button type='button' className={buttonClasses.linkButton} onClick={onClick}>
					{name}
				</button>
			);
		}

		return (
			<p>{name}</p>
		);
	};

	function StipProgressBar() {
		if (nonStip) return null;

		const hasClergyTypeStep = (stepIndex < 3) || (stepIndex > 2 && isClergy);

		return (<>
			<Tabs
				value={stepIndex > -1 ? stepIndex : 0}
				variant='scrollable'
				scrollButtons
				className='generic-stepper-bar'
			>
				<ProgressBarStepItem
					stepNameNode={getStepNameNode((newHireProps?.personalInfoStepName || PersonalInfo), stepIndex > 0, personalInfoClick)}
					progress={getProgress(0, stepIndex)}
					stepId={getStepId(0, stepIndex)}
				/>

				<ProgressBarStepItem
					stepNameNode={getStepNameNode(newHireProps?.stipEmploymentTypeStepName || EmploymentTypeDefaultLabel, stepIndex > 1, employmentTypeClick)}
					progress={getProgress(1, stepIndex)}
					stepId={getStepId(1, stepIndex)}
				/>

				{
					hasClergyTypeStep &&
					<ProgressBarStepItem
						stepNameNode={getStepNameNode(newHireProps?.stipClergyTypeStepName || ClergyTypeDefaultLabel, stepIndex > 2, clergyTypeClick)}
						progress={stepIndex < 2 ? newHireProps?.stipClergyTypeStepIfApplicable || IfApplicable : getProgress(2, stepIndex)}
						stepId={getStepId(2, stepIndex)}
					/>
				}

				<ProgressBarStepItem
					stepNameNode={getStepNameNode(newHireProps?.employmentCompensationStepName || EmploymentCompensation, false, null)}
					progress={getProgress(3, stepIndex)}
					stepId={getStepId(3, stepIndex)}
				/>
			</Tabs>
		</>);
	}

	function NonStipProgressBar() {
		if (!nonStip) return null;

		return (<>
			<Tabs
				value={stepIndex > -1 ? stepIndex : 0}
				variant='scrollable'
				scrollButtons
				className='generic-stepper-bar'
			>
				<ProgressBarStepItem
					stepNameNode={getStepNameNode((newHireProps?.personalInfoStepName || PersonalInfo), stepIndex > 0, personalInfoClick)}
					progress={getProgress(0, stepIndex)}
					stepId={getStepId(0, stepIndex)}
				/>

				<ProgressBarStepItem
					stepNameNode={getStepNameNode(newHireProps?.nonStipEmploymentTypeStepName || EmploymentTypeDefaultLabel, stepIndex > 1, employmentTypeClick)}
					progress={getProgress(1, stepIndex)}
					stepId={getStepId(1, stepIndex)}
				/>

				<ProgressBarStepItem
					stepNameNode={getStepNameNode(newHireProps?.nonStipPositionStepName || NonStipPosition, false, null)}
					progress={getProgress(2, stepIndex, `${newHireProps?.nonStipSelectedProgressLabel} ${subProgressName}`)}
					stepId={getStepId(2, stepIndex)}
				/>
			</Tabs>
		</>);
	}

	return (
		<div className={classes.genericStepperContainer}>
			<StipProgressBar />
			<NonStipProgressBar />
		</div>
	);

}